import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { FormSection } from '../ui/FormSection';
import { fetchAiUsageDetailed, fetchReportJobs } from '../../api/reportApi';
import { AI_PROVIDERS, AI_PROVIDER_LABEL, type AiUsageDetailed, type ReportJob, type ReportJobStatus } from '../../types';

const JOBS_PAGE = 10;

const JOB_STATUS_LABEL: Record<ReportJobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

function formatDate(ms: number) {
  return ms > 0 ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

// Self-service "API usage" section (AI Settings, per channel): the
// caller's own per-provider call/token usage (mirrors ZMAP's per-user AI
// tab, just scoped to the viewer instead of admin-selected) plus this
// channel's report-job queue, paginated. A regular owner has no
// cross-channel view today - see AiSettingsScreen.tsx's mount point for
// why this lives here rather than as a separate screen.
export function ApiUsageSection({ nickname }: { nickname: string }) {
  const [usage, setUsage] = useState<AiUsageDetailed | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [jobOffset, setJobOffset] = useState(0);
  const [jobs, setJobs] = useState<ReportJob[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);

  const loadUsage = () => {
    setUsageError(null);
    fetchAiUsageDetailed()
      .then(setUsage)
      .catch((err) => setUsageError(err instanceof Error ? err.message : 'Could not load usage.'));
  };

  const loadJobs = (offset: number) => {
    setJobsLoading(true);
    setJobsError(null);
    fetchReportJobs(nickname, { offset, limit: JOBS_PAGE })
      .then((page) => {
        setJobs(page.jobs);
        setJobsTotal(page.total);
      })
      .catch((err) => setJobsError(err instanceof Error ? err.message : 'Could not load jobs.'))
      .finally(() => setJobsLoading(false));
  };

  useEffect(() => {
    loadUsage();
    loadJobs(0);
    setJobOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname]);

  const refresh = () => {
    loadUsage();
    loadJobs(jobOffset);
  };

  const providers = AI_PROVIDERS.filter((p) => usage?.[p]);
  const total = usage?.total;

  return (
    <FormSection title="API usage" collapsible id="report-api-usage" defaultOpen={false}>
      <div className="flex items-center justify-between">
        <p className="text-xs text-black/50 dark:text-white/50 m-0">
          Calls and tokens spent across every provider, and this channel's report job queue.
        </p>
        <button
          type="button"
          onClick={refresh}
          disabled={jobsLoading}
          aria-label="Refresh"
          className="inline-flex items-center gap-1 text-[11px] text-black/50 dark:text-white/50 hover:text-blue-600 disabled:opacity-50 shrink-0"
        >
          <RotateCcw size={12} className={jobsLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {usageError && <p className="text-xs text-red-500 m-0">{usageError}</p>}
      {!usage && !usageError && <p className="text-xs text-black/40 dark:text-white/40 m-0">Loading…</p>}
      {usage && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-black/40 dark:text-white/40 border-b border-black/10 dark:border-white/10">
                <th className="py-1 pr-3 font-medium">Provider</th>
                <th className="py-1 pr-3 font-medium">Calls</th>
                <th className="py-1 pr-3 font-medium">Tokens in</th>
                <th className="py-1 font-medium">Tokens out</th>
              </tr>
            </thead>
            <tbody>
              {providers.length === 0 && !total && (
                <tr>
                  <td colSpan={4} className="py-2 text-black/40 dark:text-white/40">
                    No usage yet.
                  </td>
                </tr>
              )}
              {providers.map((p) => (
                <tr key={p} className="border-b border-black/5 dark:border-white/5 last:border-0">
                  <td className="py-1.5 pr-3">{AI_PROVIDER_LABEL[p]}</td>
                  <td className="py-1.5 pr-3 font-mono">{usage[p]?.calls ?? 0}</td>
                  <td className="py-1.5 pr-3 font-mono">{usage[p]?.tokensIn ?? 0}</td>
                  <td className="py-1.5 font-mono">{usage[p]?.tokensOut ?? 0}</td>
                </tr>
              ))}
              {total && (
                <tr className="font-semibold">
                  <td className="py-1.5 pr-3">Total</td>
                  <td className="py-1.5 pr-3 font-mono">{total.calls}</td>
                  <td className="py-1.5 pr-3 font-mono">{total.tokensIn}</td>
                  <td className="py-1.5 font-mono">{total.tokensOut}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-black/10 dark:border-white/10 pt-2 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Report job queue</span>
          <span className="text-[11px] text-black/40 dark:text-white/40">
            {jobsTotal > 0 ? `${jobOffset + 1}-${Math.min(jobOffset + JOBS_PAGE, jobsTotal)} of ${jobsTotal}` : '0'}
          </span>
        </div>
        {jobsError && <span className="text-xs text-red-500">{jobsError}</span>}
        {jobsLoading && jobs.length === 0 ? (
          <span className="text-xs text-black/40 dark:text-white/40">Loading…</span>
        ) : jobs.length === 0 ? (
          <span className="text-xs text-black/40 dark:text-white/40">No jobs yet.</span>
        ) : (
          <ul className="flex flex-col divide-y divide-black/5 dark:divide-white/5">
            {jobs.map((j) => (
              <li key={j.id} className="flex items-center gap-2 py-1.5 text-xs">
                <span className="w-16 shrink-0 font-medium text-black/70 dark:text-white/70">{JOB_STATUS_LABEL[j.status]}</span>
                <span className="flex-1 truncate text-black/50 dark:text-white/50">
                  {AI_PROVIDER_LABEL[j.provider] ?? j.provider}
                  {j.mode === 'questions' ? ' · questions' : j.mode === 'suggest' ? ' · suggest' : ''}
                </span>
                <span className="shrink-0 text-black/40 dark:text-white/40">{formatDate(j.enqueuedAt)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              const next = Math.max(0, jobOffset - JOBS_PAGE);
              setJobOffset(next);
              loadJobs(next);
            }}
            disabled={jobsLoading || jobOffset === 0}
            className="rounded-md px-2.5 py-1 border border-black/15 dark:border-white/20 text-[11px] disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => {
              const next = jobOffset + JOBS_PAGE;
              setJobOffset(next);
              loadJobs(next);
            }}
            disabled={jobsLoading || jobOffset + JOBS_PAGE >= jobsTotal}
            className="rounded-md px-2.5 py-1 border border-black/15 dark:border-white/20 text-[11px] disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </FormSection>
  );
}
